import { z } from "zod";
import { FinanceExplorerOptionsSchema, MonthlyPnlMatrixFiltersSchema, financeExplorerColumns } from "@chro/shared";

const month = z.string().regex(/^(20\d{2}|[3-9]\d{3})-(0[1-9]|1[0-2])$/);
const token = z.string().regex(/^[a-zA-Z0-9-]{1,80}$/);
export type FinanceOrigin = { financeMonth?: string; financeView?: string; financeCompany?: string; financeTab?: "matrix" };

export function parseFinanceOrigin(search: Record<string, unknown>): FinanceOrigin {
  const financeMonth = month.safeParse(search.financeMonth);
  const financeView = token.safeParse(search.financeView);
  const financeCompany = token.safeParse(search.financeCompany);
  return {
    ...(search.financeTab === "matrix" ? { financeTab: "matrix" as const } : {}),
    ...(financeMonth.success ? { financeMonth: financeMonth.data } : {}),
    ...(financeView.success ? { financeView: financeView.data } : {}),
    ...(financeCompany.success ? { financeCompany: financeCompany.data } : {}),
  };
}

export function financeReturnSearch(origin: FinanceOrigin): FinanceOrigin {
  return parseFinanceOrigin(origin);
}

export function canReturnToFinance(origin: FinanceOrigin, currentCompany: string | null): boolean {
  return (!!origin.financeMonth || origin.financeTab === "matrix") && (!origin.financeCompany || origin.financeCompany === currentCompany);
}

export function financeMonthLabel(value: string): string {
  return month.safeParse(value).success
    ? new Date(`${value}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })
    : "Finance";
}

export const FinanceViewSchema = z.object({
  version: z.literal(1), companyId: token, month, from: month, to: month,
  options: FinanceExplorerOptionsSchema,
  focusedMetric: z.enum(["statementVm", "statementDifference", "unreconciledVm", ...financeExplorerColumns.map(column => column.key)]).optional(),
  focusedCurrency: z.string().min(1).max(12).optional(),
  expanded: z.array(z.string().max(2000)).max(10000),
  selectedId: z.string().max(2000).nullable(), fullBridge: z.boolean(),
  inputVersion: z.string().regex(/^[a-f0-9]{64}$/), savedAt: z.number().finite(),
});
export type FinanceView = z.infer<typeof FinanceViewSchema>;
export const MatrixViewSchema = z.object({
  version: z.literal(1), kind: z.literal("matrix"), companyId: token,
  filters: MonthlyPnlMatrixFiltersSchema, focusMonth: month.nullable(),
  overviewMode: z.enum(["cards", "comparison", "overview", "bridge"]).optional(),
  selectedRow: z.string().max(2000).nullable(),
  expanded: z.array(z.string().max(5000)).max(10000).optional(),
  columns: z.array(z.enum(["product", "period", "book", "trader", "date"])).max(5).optional(),
  comparison: z.object({
    mode: z.enum(["compare", "explain"]), metric: z.enum(["recognizedPnl", "calculatedVm", "openingCarry", "closingCarry"]),
    currency: z.string().min(1).max(20), month, fullBridge: z.boolean().optional(), bridgeCharts: z.boolean().optional(),
    bridge: z.object({ from: month.optional(), to: month.optional(), books: z.array(z.string().max(2000).nullable()).max(500).optional(), traders: z.array(z.string().max(2000).nullable()).max(500).optional() }).optional(),
    selection: z.object({ month: month.nullable(), range: z.object({ from: month, to: month }).optional(), rowIds: z.array(z.string().max(2000)).max(10000), label: z.string().max(2000),
      metric: z.enum(["statementVm", "statementDifference", "unreconciledVm", ...financeExplorerColumns.map(column => column.key)]),
      inputVersion: z.string().regex(/^[a-f0-9]{64}$/), selectedId: z.string().max(2000).nullable(), options: FinanceExplorerOptionsSchema,
    }).nullable(),
  }).optional(),
  referenceContext: z.object({ targetRowId: z.string().max(2000), product: z.string().nullable(), period: z.string().nullable() }).nullable().optional(),
  scrollTop: z.number().finite().min(0).max(10000000), scrollLeft: z.number().finite().min(0).max(10000000),
  savedAt: z.number().finite(),
});
export type MatrixView = z.infer<typeof MatrixViewSchema>;
type ViewStorage = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
const prefix = "cascena.finance-view.v1:";
const maximumAge = 24 * 60 * 60 * 1000;

/** Open synchronously from the click, preserving the source tab and its explanation. */
export function openFinanceTab(href: string, origin: FinanceOrigin): void {
  const destination = new URL(href, window.location.href);
  if (destination.origin !== window.location.origin) throw new Error("Invalid Finance destination.");
  const tab = window.open("about:blank", "_blank");
  if (!tab) throw new Error("Allow new tabs for CASCENA, then open the source again.");
  tab.opener = null;
  // Copy only the return-view preference explicitly; noopener tabs do not inherit it.
  try {
    if (origin.financeView) {
      const key = prefix + origin.financeView;
      const snapshot = browserStorage()?.getItem(key);
      if (snapshot) tab.sessionStorage.setItem(key, snapshot);
    }
  } catch { /* Source navigation still works when browser storage is unavailable. */ }
  tab.location.replace(destination.href);
}

function browserStorage(): ViewStorage | null {
  try { return typeof window === "undefined" ? null : window.sessionStorage; }
  catch { return null; }
}

/** Only navigation preferences are stored. Monetary values are always reloaded. */
export function saveFinanceView(view: Omit<FinanceView, "version" | "savedAt">, storage = browserStorage(), now = Date.now()): FinanceOrigin {
  const origin = { financeMonth: view.month, financeCompany: view.companyId };
  return saveSnapshot(origin, () => FinanceViewSchema.parse({ ...view, version: 1, savedAt: now }), storage, now);
}

export function saveMatrixView(view: Omit<MatrixView, "version" | "kind" | "savedAt">, storage = browserStorage(), now = Date.now()): FinanceOrigin {
  const origin: FinanceOrigin = { financeTab: "matrix", financeCompany: view.companyId };
  return saveSnapshot(origin, () => MatrixViewSchema.parse({ ...view, version: 1, kind: "matrix", savedAt: now }), storage, now);
}

function saveSnapshot(origin: FinanceOrigin, snapshot: () => unknown, storage: ViewStorage | null, now: number): FinanceOrigin {
  if (!storage) return origin;
  try {
    const value = snapshot();
    const entries: { key: string; savedAt: number }[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(prefix)) continue;
      try { entries.push({ key, savedAt: Number(JSON.parse(storage.getItem(key) ?? "{}").savedAt) || 0 }); }
      catch { entries.push({ key, savedAt: 0 }); }
    }
    entries.sort((a, b) => b.savedAt - a.savedAt).forEach((entry, i) => {
      if (i >= 19 || now - entry.savedAt > maximumAge) storage.removeItem(entry.key);
    });
    const id = crypto.randomUUID();
    storage.setItem(prefix + id, JSON.stringify(value));
    return { ...origin, financeView: id };
  } catch { return origin; }
}

export function readMatrixView(origin: FinanceOrigin, companyId: string | null, storage = browserStorage(), now = Date.now()): MatrixView | null {
  if (!storage || origin.financeTab !== "matrix" || !origin.financeView || !canReturnToFinance(origin, companyId)) return null;
  try {
    const view = MatrixViewSchema.parse(JSON.parse(storage.getItem(prefix + origin.financeView) ?? "null"));
    if (view.companyId !== companyId || now - view.savedAt > maximumAge || view.savedAt > now + 60000) return null;
    return view;
  } catch { return null; }
}

export function readFinanceView(origin: FinanceOrigin, companyId: string | null, storage = browserStorage(), now = Date.now()): FinanceView | null {
  if (!storage || !origin.financeView || !canReturnToFinance(origin, companyId)) return null;
  try {
    const view = FinanceViewSchema.parse(JSON.parse(storage.getItem(prefix + origin.financeView) ?? "null"));
    if (view.companyId !== companyId || view.month !== origin.financeMonth || now - view.savedAt > maximumAge || view.savedAt > now + 60000) return null;
    return view;
  } catch { return null; }
}
